import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Alert, Chip, Button, Grid, FormControl, InputLabel,
  Select, MenuItem, Checkbox, ListItemText, Switch, FormControlLabel,
  Stepper, Step, StepLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  Drawer, LinearProgress, Snackbar, Tooltip, IconButton, Card, CardContent,
  Divider, FormHelperText
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import CalculateIcon from '@mui/icons-material/Calculate';
import TextField from '@mui/material/TextField';
import { verificationService } from '../services/verificationService';
import { approvalService } from '../services/approvalService';
import { eledgerService } from '../services/eledgerService';
import NCCorrectionPanel from './NCCorrectionPanel';

const ENTITY_TYPES = [
  { value: 'direct_emission', label: 'Direct Emissions' },
  { value: 'embedded_emission', label: 'Embedded Emissions' },
  { value: 'allocation_execution', label: 'Allocation Executions' },
  { value: 'e_liability_transfer', label: 'E-Ledger Transfers' }
];

const ALLOCATION_METHODS = [
  { value: 'PHYSICAL_CAUSALITY', label: 'Physical Causality' },
  { value: 'ECONOMIC_VALUE', label: 'Economic Value' },
  { value: 'NONE', label: 'None (100% allocation)' }
];

const DVE_STAGES = [
  'upload', 'ingestion', 'rule_extraction', 'mapping',
  'matching', 'validation', 'verification', 'complete'
];

const STATUS_COLORS = {
  pending: 'default',
  processing: 'info',
  completed: 'success',
  failed: 'error',
  manual_review: 'warning'
};

function VerificationDashboard() {
  // -- Trigger panel state --
  const [entityType, setEntityType] = useState('direct_emission');
  const [pendingEntities, setPendingEntities] = useState([]);
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  const [skipDVE, setSkipDVE] = useState(true);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [loadingEntities, setLoadingEntities] = useState(false);

  // -- Sessions state --
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  // -- Session detail state --
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionResults, setSessionResults] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // -- Result drawer state --
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);

  // -- DVE health --
  const [dveHealthy, setDveHealthy] = useState(null);

  // -- Notifications --
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);

  // -- PCF Verification state --
  const [pcfDialogOpen, setPcfDialogOpen] = useState(false);
  const [pcfLoading, setPcfLoading] = useState(false);
  const [pcfForm, setPcfForm] = useState({
    eLedgerId: '',
    productType: '',
    productionVolume: '',
    productionUnit: 'kg',
    allocationMethod: 'PHYSICAL_CAUSALITY',
    allocationPercentage: '100',
    skipDVE: true
  });
  
  // -- PCF Dropdown data (facility-based per PACT) --
  const [facilityLedgers, setFacilityLedgers] = useState([]);
  const [ofpProducts, setOfpProducts] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  
  // -- PCF Result dialog state --
  const [pcfResultOpen, setPcfResultOpen] = useState(false);
  const [pcfResult, setPcfResult] = useState(null);

  // ── Load sessions on mount and filter change ──
  const loadSessions = useCallback(async () => {
    try {
      setSessionsLoading(true);
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      const data = await verificationService.getSessions(filters);
      setSessions(data ?? []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || String(err));
    } finally {
      setSessionsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // ── Load pending entities when entity type changes ──
  useEffect(() => {
    const loadPending = async () => {
      try {
        setLoadingEntities(true);
        setSelectedEntityIds([]);
        const data = await approvalService.getPending(entityType);
        setPendingEntities(data ?? []);
      } catch {
        setPendingEntities([]);
      } finally {
        setLoadingEntities(false);
      }
    };
    loadPending();
  }, [entityType]);

  // ── Check dVE health on mount ──
  useEffect(() => {
    verificationService.checkDVEHealth()
      .then(() => setDveHealthy(true))
      .catch(() => setDveHealthy(false));
  }, []);

  // ── Load PCF dropdown data when dialog opens ──
  useEffect(() => {
    if (!pcfDialogOpen) return;
    
    const loadDropdownData = async () => {
      try {
        setLoadingDropdowns(true);
        const [ledgers, products] = await Promise.all([
          eledgerService.getFacilityLedgers(),
          verificationService.getOFPProducts()
        ]);
        setFacilityLedgers(ledgers ?? []);
        setOfpProducts(products ?? []);
      } catch (err) {
        console.error('Failed to load PCF dropdown data:', err);
        setError('Failed to load facility and product data');
      } finally {
        setLoadingDropdowns(false);
      }
    };
    loadDropdownData();
  }, [pcfDialogOpen]);

  // ── Helpers ──
  const getPKField = (type) => {
    const map = {
      direct_emission: 'direct_emission_id',
      allocation_execution: 'allocation_execution_id',
      e_liability_transfer: 'transfer_id',
      embedded_emission: 'embedded_emission_id'
    };
    return map[type] || 'id';
  };

  // ── Actions ──
  const handleStartVerification = async () => {
    if (selectedEntityIds.length === 0) return;
    try {
      setTriggerLoading(true);
      setError(null);
      const result = await verificationService.startVerification(
        entityType, selectedEntityIds, { skipDVE }
      );
      setSnackbar({
        open: true,
        message: `Verification started for ${result.totalEntities} entities (Session: ${result.verificationSessionId.slice(0, 8)}...)`,
        severity: 'success'
      });
      setSelectedEntityIds([]);
      loadSessions();
    } catch (err) {
      setError(err.response?.data?.error || err.message || String(err));
    } finally {
      setTriggerLoading(false);
    }
  };

  const handleOpenDetail = async (session) => {
    setSelectedSession(session);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const results = await verificationService.getSessionResults(session.verification_session_id);
      setSessionResults(results ?? null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || String(err));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleProcessResults = async (sessionId) => {
    try {
      await verificationService.processResults(sessionId);
      setSnackbar({ open: true, message: 'Results processed successfully', severity: 'success' });
      loadSessions();
      if (selectedSession?.verification_session_id === sessionId) {
        const results = await verificationService.getSessionResults(sessionId);
        setSessionResults(results);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || String(err));
    }
  };

  const handleReVerify = async (sessionId) => {
    try {
      const reason = prompt('Reason for re-verification (optional):');
      const result = await verificationService.reVerifySession(sessionId, {
        skipDVE,
        reason: reason || 'Manual re-verification',
        actorName: 'User'
      });
      setSnackbar({
        open: true,
        message: `Re-verification started (Session: ${result.verificationSessionId.slice(0, 8)}...)`,
        severity: 'success'
      });
      setDetailOpen(false);
      loadSessions();
    } catch (err) {
      setError(err.response?.data?.error || err.message || String(err));
    }
  };

  const handleGenerateTestData = async () => {
    try {
      const count = parseInt(prompt('How many test emissions to generate?', '3') || '3', 10);
      const result = await verificationService.generateTestData(count, 'all');
      setSnackbar({
        open: true,
        message: result.message,
        severity: 'success'
      });
      // Reload entities
      const data = await approvalService.getPending(entityType);
      setPendingEntities(data ?? []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || String(err));
    }
  };

  const handleOpenResult = (result) => {
    // Include entityType from sessionResults for NC panel
    setSelectedResult({
      ...result,
      entityType: sessionResults?.entityType || selectedSession?.entity_type
    });
    setDrawerOpen(true);
  };

  // ── PCF Verification handlers ──
  const handlePcfFormChange = (field) => (event) => {
    const value = event.target.value;
    setPcfForm(prev => ({ ...prev, [field]: value }));
  };

  const handleStartPCFVerification = async () => {
    if (!pcfForm.eLedgerId || !pcfForm.productionVolume) {
      setError('E-Ledger ID and Production Volume are required');
      return;
    }
    try {
      setPcfLoading(true);
      setError(null);
      const result = await verificationService.startPCFVerification(pcfForm.eLedgerId, {
        productType: pcfForm.productType,
        productionVolume: parseFloat(pcfForm.productionVolume),
        productionUnit: pcfForm.productionUnit,
        allocationMethod: pcfForm.allocationMethod,
        allocationPercentage: parseFloat(pcfForm.allocationPercentage),
        skipDVE: pcfForm.skipDVE
      });
      
      // Show PCF result dialog with full calculation breakdown
      setPcfResult(result);
      setPcfResultOpen(true);
      setPcfDialogOpen(false);
      setPcfForm({
        eLedgerId: '',
        productType: '',
        productionVolume: '',
        productionUnit: 'kg',
        allocationMethod: 'PHYSICAL_CAUSALITY',
        allocationPercentage: '100',
        skipDVE: true
      });
      loadSessions();
    } catch (err) {
      setError(err.response?.data?.error || err.message || String(err));
    } finally {
      setPcfLoading(false);
    }
  };

  const getActiveStageIndex = (stage) => {
    const idx = DVE_STAGES.indexOf(stage);
    return idx >= 0 ? idx : 0;
  };

  const toggleSelectAll = () => {
    const pk = getPKField(entityType);
    if (selectedEntityIds.length === pendingEntities.length) {
      setSelectedEntityIds([]);
    } else {
      setSelectedEntityIds(pendingEntities.map(e => e[pk]));
    }
  };

  // ── Render ──
  return (
    <Box>
      <Typography variant="h4" gutterBottom>AI Agent Verification</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Automated verification of E-ledger entries using rule-based analysis and dVE integration
      </Typography>

      {/* DVE Health Indicator */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <FiberManualRecordIcon
          sx={{ fontSize: 12, color: dveHealthy === null ? 'grey.400' : dveHealthy ? 'success.main' : 'error.main' }}
        />
        <Typography variant="body2" color="text.secondary">
          dVE Engine: {dveHealthy === null ? 'Checking...' : dveHealthy ? 'Connected' : 'Unavailable (local rules only)'}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{typeof error === 'object' && error !== null ? (error.message || JSON.stringify(error)) : error}</Alert>}

      {/* ── PCF VERIFICATION CARD ── */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalculateIcon color="primary" />
            <Box>
              <Typography variant="h6">Product Carbon Footprint (PCF)</Typography>
              <Typography variant="body2" color="text.secondary">
                Calculate and verify PCF using PACT PathFinder methodology
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<CalculateIcon />}
            onClick={() => setPcfDialogOpen(true)}
          >
            Verify PCF
          </Button>
        </Box>
      </Paper>

      {/* ── 1. TRIGGER PANEL ── */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Start Verification</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PlayArrowIcon />}
            onClick={handleGenerateTestData}
          >
            Generate Test Data
          </Button>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Entity Type</InputLabel>
              <Select
                value={entityType}
                label="Entity Type"
                onChange={(e) => setEntityType(e.target.value)}
              >
                {ENTITY_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={7}>
            {loadingEntities ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading pending entities...</Typography>
              </Box>
            ) : pendingEntities.length === 0 ? (
              <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
                No pending entities for {ENTITY_TYPES.find(t => t.value === entityType)?.label}
              </Alert>
            ) : (
              <Box>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Entities ({selectedEntityIds.length}/{pendingEntities.length})</InputLabel>
                  <Select
                    multiple
                    value={selectedEntityIds}
                    label={`Select Entities (${selectedEntityIds.length}/${pendingEntities.length})`}
                    onChange={(e) => setSelectedEntityIds(e.target.value)}
                    renderValue={(selected) => `${selected.length} selected`}
                  >
                    {pendingEntities.map((entity) => {
                      const pk = getPKField(entityType);
                      const id = entity[pk];
                      return (
                        <MenuItem key={id} value={id}>
                          <Checkbox checked={selectedEntityIds.includes(id)} size="small" />
                          <ListItemText
                            primary={id?.slice(0, 12) + '...'}
                            secondary={entity.created_at ? new Date(entity.created_at).toLocaleDateString() : ''}
                          />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Button size="small" onClick={toggleSelectAll} sx={{ mt: 0.5 }}>
                  {selectedEntityIds.length === pendingEntities.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={2}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!skipDVE}
                  onChange={(e) => setSkipDVE(!e.target.checked)}
                />
              }
              label="Verify via dVE"
              sx={{ mb: 0.5 }}
            />
            <Button
              variant="contained"
              startIcon={triggerLoading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleStartVerification}
              disabled={selectedEntityIds.length === 0 || triggerLoading}
              fullWidth
            >
              Verify ({selectedEntityIds.length})
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ── 2. SESSIONS TABLE ── */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Verification Sessions</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            <IconButton onClick={loadSessions} size="small">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {sessionsLoading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : sessions.length === 0 ? (
          <Alert severity="info">No verification sessions found. Start one above.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Session ID</TableCell>
                <TableCell>Entity Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell align="center">Total</TableCell>
                <TableCell align="center">Approved</TableCell>
                <TableCell align="center">Rejected</TableCell>
                <TableCell align="center">Flagged</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => (
                <TableRow
                  key={session.verification_session_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleOpenDetail(session)}
                >
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {session.verification_session_id?.slice(0, 8)}...
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={session.entity_type?.replace(/_/g, ' ')}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={session.status}
                      size="small"
                      color={STATUS_COLORS[session.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {session.current_stage?.replace(/_/g, ' ') || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{session.total_entities}</TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="success.main" fontWeight={600}>
                      {session.entities_approved || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="error.main" fontWeight={600}>
                      {session.entities_rejected || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="warning.main" fontWeight={600}>
                      {session.entities_flagged || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {session.started_at ? new Date(session.started_at).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleOpenDetail(session)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {session.status === 'processing' && (
                        <Tooltip title="Process Results Now">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleProcessResults(session.verification_session_id)}
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(session.status === 'completed' || session.status === 'failed') && 
                       !(session.status === 'completed' && session.entities_approved === session.total_entities) && (
                        <Tooltip title="Re-verify">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleReVerify(session.verification_session_id)}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* ── 3. SESSION DETAIL DIALOG ── */}
      <Dialog
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setSessionResults(null); }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Session Detail
          {selectedSession && (
            <Typography variant="body2" color="text.secondary">
              {selectedSession.verification_session_id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : sessionResults ? (
            <Box>
              {/* dVE Workflow Stage Stepper */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>dVE Workflow Progress</Typography>
                <Stepper
                  activeStep={getActiveStageIndex(selectedSession?.current_stage)}
                  alternativeLabel
                  sx={{ '& .MuiStepLabel-label': { fontSize: '0.7rem' } }}
                >
                  {DVE_STAGES.map((stage) => (
                    <Step key={stage} completed={
                      DVE_STAGES.indexOf(stage) < getActiveStageIndex(selectedSession?.current_stage) ||
                      selectedSession?.status === 'completed'
                    }>
                      <StepLabel>{stage.replace(/_/g, ' ')}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
                {selectedSession?.verification_config?.skipDVE || selectedSession?.metadata?.dve_skipped ? (
                  <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                    dVE skipped - local rule engine used
                  </Alert>
                ) : null}
              </Paper>

              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">Total Entities</Typography>
                      <Typography variant="h5">{sessionResults.totalEntities}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined" sx={{ borderColor: 'success.main' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">Approved</Typography>
                      <Typography variant="h5" color="success.main">
                        {sessionResults.entitiesApproved || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined" sx={{ borderColor: 'error.main' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">Rejected</Typography>
                      <Typography variant="h5" color="error.main">
                        {sessionResults.entitiesRejected || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined" sx={{ borderColor: 'warning.main' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">Flagged</Typography>
                      <Typography variant="h5" color="warning.main">
                        {sessionResults.entitiesFlagged || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Completion bar */}
              {sessionResults.totalEntities > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption">Verification Progress</Typography>
                    <Typography variant="caption">
                      {((sessionResults.entitiesApproved || 0) + (sessionResults.entitiesRejected || 0) + (sessionResults.entitiesFlagged || 0))}/{sessionResults.totalEntities}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      (((sessionResults.entitiesApproved || 0) + (sessionResults.entitiesRejected || 0) + (sessionResults.entitiesFlagged || 0)) /
                        sessionResults.totalEntities) * 100
                    }
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              )}

              {/* Entity Results Table */}
              <Typography variant="subtitle2" gutterBottom>Entity Results</Typography>
              {(sessionResults.entityResults || []).length === 0 ? (
                <Alert severity="info">No entity results yet. Session may still be processing.</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Entity ID</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell align="center">Violations</TableCell>
                      <TableCell>Checks</TableCell>
                      <TableCell>Approval/NCs</TableCell>
                      <TableCell>Attestation</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(sessionResults.entityResults || []).map((result, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {result.entityId?.slice(0, 12)}...
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={
                              result.action === 'approve' ? <CheckCircleIcon /> :
                              result.action === 'reject' ? <CancelIcon /> : <WarningIcon />
                            }
                            label={result.action?.replace(/_/g, ' ')}
                            size="small"
                            color={
                              result.action === 'approve' ? 'success' :
                              result.action === 'reject' ? 'error' : 'warning'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={(result.confidence || 0) * 100}
                              sx={{
                                width: 60, height: 6, borderRadius: 1,
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor:
                                    result.confidence >= 0.9 ? 'success.main' :
                                    result.confidence >= 0.7 ? 'warning.main' : 'error.main'
                                }
                              }}
                            />
                            <Typography variant="caption">
                              {((result.confidence || 0) * 100).toFixed(0)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {result.violationCount > 0 ? (
                            <Chip label={result.violationCount} size="small" color="error" variant="outlined" />
                          ) : (
                            <Chip label="0" size="small" color="success" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          {(result.principleChecks || []).map((pc, i) => (
                            <Tooltip key={i} title={`${pc.principle}: ${pc.details}`}>
                              {pc.passed ? (
                                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.25 }} />
                              ) : (
                                <CancelIcon sx={{ fontSize: 16, color: 'error.main', mr: 0.25 }} />
                              )}
                            </Tooltip>
                          ))}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {result.approvalId && (
                              <Tooltip title={`Approval ID: ${result.approvalId}`}>
                                <Chip
                                  icon={<InfoIcon />}
                                  label="Approval"
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                              </Tooltip>
                            )}
                            {(result.nonConformanceIds || []).length > 0 && (
                              <Tooltip title={`${result.nonConformanceIds.length} Non-Conformance(s)`}>
                                <Chip
                                  icon={<WarningIcon />}
                                  label={`${result.nonConformanceIds.length} NC`}
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {result.attestation && result.attestation.attestation_id ? (
                            <Tooltip title={`Attestation: ${result.attestation.attestation_id.slice(0, 8)}... | ${result.attestation.algorithm}`}>
                              <Chip
                                icon={<FingerprintIcon />}
                                label="Signed"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.disabled">-</Typography>
                          )}
                          {result.credential && result.credential.credentialId && (
                            <Tooltip title={`VC: ${result.credential.credentialId.slice(0, 12)}...`}>
                              <Chip
                                label="VC"
                                size="small"
                                color="info"
                                sx={{ fontSize: '0.6rem', height: 18, ml: 0.5 }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleOpenResult(result)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {/* Timing info */}
              <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Started: {sessionResults.startedAt ? new Date(sessionResults.startedAt).toLocaleString() : '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Completed: {sessionResults.completedAt ? new Date(sessionResults.completedAt).toLocaleString() : '-'}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Alert severity="warning">Could not load session results.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          {selectedSession && (selectedSession.status === 'completed' || selectedSession.status === 'failed') && (
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => handleReVerify(selectedSession.verification_session_id)}
              color="primary"
              variant="outlined"
            >
              Re-verify
            </Button>
          )}
          <Button onClick={() => { setDetailOpen(false); setSessionResults(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── PCF VERIFICATION DIALOG ── */}
      <Dialog
        open={pcfDialogOpen}
        onClose={() => setPcfDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalculateIcon color="primary" />
            <Typography variant="h6">PCF Verification</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Calculate Product Carbon Footprint using PACT PathFinder methodology
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {loadingDropdowns && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Loading facilities and products...</Typography>
              </Box>
            )}
            
            <FormControl fullWidth required>
              <InputLabel>Facility (PACT-compliant)</InputLabel>
              <Select
                value={pcfForm.eLedgerId}
                label="Facility (PACT-compliant)"
                onChange={handlePcfFormChange('eLedgerId')}
                disabled={loadingDropdowns}
              >
                {facilityLedgers.map((ledger) => (
                  <MenuItem key={ledger.e_ledger_id} value={ledger.e_ledger_id}>
                    {ledger.facility_name} ({ledger.organization_name})
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>PCF calculated from facility-specific emissions per PACT methodology</FormHelperText>
            </FormControl>
            
            <FormControl fullWidth required>
              <InputLabel>Product</InputLabel>
              <Select
                value={pcfForm.productType}
                label="Product"
                onChange={handlePcfFormChange('productType')}
                disabled={loadingDropdowns}
              >
                {ofpProducts.map((product) => (
                  <MenuItem key={product.product_id} value={product.product_id}>
                    {product.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>OFP product from master_data.product</FormHelperText>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField
                  label="Production Volume"
                  type="number"
                  value={pcfForm.productionVolume}
                  onChange={handlePcfFormChange('productionVolume')}
                  fullWidth
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Total production volume for PCF denominator"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>Unit</InputLabel>
                  <Select
                    value={pcfForm.productionUnit}
                    label="Unit"
                    onChange={handlePcfFormChange('productionUnit')}
                  >
                    <MenuItem value="bbl">bbl</MenuItem>
                    <MenuItem value="mscf">mscf</MenuItem>
                    <MenuItem value="t">t (metric ton)</MenuItem>
                    <MenuItem value="tonne">tonne</MenuItem>
                    <MenuItem value="kg">kg</MenuItem>
                    <MenuItem value="MWh">MWh</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">Allocation Settings (PACT)</Typography>

            <FormControl fullWidth>
              <InputLabel>Allocation Method</InputLabel>
              <Select
                value={pcfForm.allocationMethod}
                label="Allocation Method"
                onChange={handlePcfFormChange('allocationMethod')}
              >
                {ALLOCATION_METHODS.map(m => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Allocation Percentage"
              type="number"
              value={pcfForm.allocationPercentage}
              onChange={handlePcfFormChange('allocationPercentage')}
              fullWidth
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              helperText="Percentage of emissions allocated to this product (0-100%)"
            />

            <Divider sx={{ my: 1 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={pcfForm.skipDVE}
                  onChange={(e) => setPcfForm(prev => ({ ...prev, skipDVE: e.target.checked }))}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Skip dVE External Verification</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {pcfForm.skipDVE ? 'Local calculation only' : 'Submit to dVE for PACT compliance verification'}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPcfDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={pcfLoading ? <CircularProgress size={16} color="inherit" /> : <CalculateIcon />}
            onClick={handleStartPCFVerification}
            disabled={pcfLoading || !pcfForm.eLedgerId || !pcfForm.productType || !pcfForm.productionVolume}
          >
            Calculate & Verify PCF
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── PCF RESULT DIALOG ── */}
      <Dialog
        open={pcfResultOpen}
        onClose={() => setPcfResultOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6">PCF Calculation Complete</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {pcfResult?.pcfCalculation && (
            <Box sx={{ mt: 1 }}>
              {/* Summary Card */}
              <Card sx={{ mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
                <CardContent>
                  <Typography variant="h3" align="center" sx={{ fontWeight: 'bold' }}>
                    {pcfResult.pcfCalculation.calculatedPCF?.toFixed(4)}
                  </Typography>
                  <Typography variant="h6" align="center">
                    {pcfResult.pcfCalculation.pcfUnit}
                  </Typography>
                  <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                    Product Carbon Footprint for {pcfResult.pcfCalculation.productType}
                  </Typography>
                </CardContent>
              </Card>

              {/* Production Info */}
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Production Details</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Production Volume</Typography>
                  <Typography variant="body1">{pcfResult.pcfCalculation.productionVolume?.toLocaleString()} {pcfResult.pcfCalculation.productionUnit}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Accounting Period</Typography>
                  <Typography variant="body1">
                    {pcfResult.pcfCalculation.accountingPeriod?.start} to {pcfResult.pcfCalculation.accountingPeriod?.end}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Methodology</Typography>
                  <Typography variant="body1">{pcfResult.pcfCalculation.methodology}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Scope Breakdown */}
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Emissions Breakdown (Allocated)</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Scope</TableCell>
                    <TableCell align="right">Emissions (kgCO2e)</TableCell>
                    <TableCell align="right">% of Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: '#1976d2', borderRadius: '50%' }} />
                        Scope 1 (Direct)
                      </Box>
                    </TableCell>
                    <TableCell align="right">{pcfResult.pcfCalculation.allocatedEmissions?.scope1?.toLocaleString(undefined, {maximumFractionDigits: 2})}</TableCell>
                    <TableCell align="right">
                      {pcfResult.pcfCalculation.allocatedEmissions?.total > 0 
                        ? ((pcfResult.pcfCalculation.allocatedEmissions?.scope1 / pcfResult.pcfCalculation.allocatedEmissions?.total) * 100).toFixed(1) 
                        : 0}%
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: '#9c27b0', borderRadius: '50%' }} />
                        Scope 2 (Indirect - Electricity)
                      </Box>
                    </TableCell>
                    <TableCell align="right">{pcfResult.pcfCalculation.allocatedEmissions?.scope2?.toLocaleString(undefined, {maximumFractionDigits: 2})}</TableCell>
                    <TableCell align="right">
                      {pcfResult.pcfCalculation.allocatedEmissions?.total > 0 
                        ? ((pcfResult.pcfCalculation.allocatedEmissions?.scope2 / pcfResult.pcfCalculation.allocatedEmissions?.total) * 100).toFixed(1) 
                        : 0}%
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: '#ff9800', borderRadius: '50%' }} />
                        Scope 3 (Upstream Supply Chain)
                      </Box>
                    </TableCell>
                    <TableCell align="right">{pcfResult.pcfCalculation.allocatedEmissions?.scope3?.toLocaleString(undefined, {maximumFractionDigits: 2})}</TableCell>
                    <TableCell align="right">
                      {pcfResult.pcfCalculation.allocatedEmissions?.total > 0 
                        ? ((pcfResult.pcfCalculation.allocatedEmissions?.scope3 / pcfResult.pcfCalculation.allocatedEmissions?.total) * 100).toFixed(1) 
                        : 0}%
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total Allocated</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{pcfResult.pcfCalculation.allocatedEmissions?.total?.toLocaleString(undefined, {maximumFractionDigits: 2})}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>100%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />

              {/* Allocation Info */}
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Allocation Parameters</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Method</Typography>
                  <Chip label={pcfResult.pcfCalculation.allocation?.method} size="small" />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Allocation %</Typography>
                  <Typography variant="body1">{pcfResult.pcfCalculation.allocation?.percentage}%</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Data Quality</Typography>
                  <Chip 
                    label={`DQI: ${pcfResult.pcfCalculation.dataQualityRating}/5`} 
                    size="small" 
                    color={pcfResult.pcfCalculation.dataQualityRating <= 2 ? 'success' : pcfResult.pcfCalculation.dataQualityRating <= 3 ? 'warning' : 'error'}
                  />
                </Grid>
              </Grid>

              {/* Source Info */}
              <Typography variant="body2" color="text.secondary">
                Based on {pcfResult.pcfCalculation.sourceEmissions?.directEmissionCount} direct emissions and {pcfResult.pcfCalculation.sourceEmissions?.embeddedEmissionCount} embedded emissions
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPcfResultOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── 4. RESULT DRAWER ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 3 } }}
        sx={{ zIndex: 1400 }}  // Higher than Dialog (1300)
      >
        {selectedResult && (
          <Box>
            <Typography variant="h6" gutterBottom>Entity Verification Result</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', mb: 2 }}>
              {selectedResult.entityId}
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {/* Action & Confidence */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <Chip
                icon={
                  selectedResult.action === 'approve' ? <CheckCircleIcon /> :
                  selectedResult.action === 'reject' ? <CancelIcon /> : <WarningIcon />
                }
                label={selectedResult.action?.replace(/_/g, ' ')}
                color={
                  selectedResult.action === 'approve' ? 'success' :
                  selectedResult.action === 'reject' ? 'error' : 'warning'
                }
              />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="caption" color="text.secondary">Confidence</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(selectedResult.confidence || 0) * 100}
                    sx={{
                      flexGrow: 1, height: 10, borderRadius: 1,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor:
                          selectedResult.confidence >= 0.9 ? 'success.main' :
                          selectedResult.confidence >= 0.7 ? 'warning.main' : 'error.main'
                      }
                    }}
                  />
                  <Typography variant="body2" fontWeight={600}>
                    {((selectedResult.confidence || 0) * 100).toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Reasoning */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>Reasoning</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {selectedResult.reasoning}
              </Typography>
            </Paper>

            {/* Principle Checks */}
            <Typography variant="subtitle2" gutterBottom>Principle Checks</Typography>
            <Box sx={{ mb: 2 }}>
              {(selectedResult.principleChecks || []).map((pc, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  {pc.passed ? (
                    <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', mt: 0.2 }} />
                  ) : (
                    <CancelIcon sx={{ fontSize: 18, color: 'error.main', mt: 0.2 }} />
                  )}
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{pc.principle}</Typography>
                    <Typography variant="caption" color="text.secondary">{pc.details}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Violations */}
            {selectedResult.violationCount > 0 && (
              <Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" gutterBottom color="error.main">
                  Violations ({selectedResult.violationCount})
                </Typography>
                {/* Pull violations from principleChecks that failed */}
                {(selectedResult.principleChecks || [])
                  .filter(pc => !pc.passed)
                  .map((pc, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1, borderColor: 'error.light' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>{pc.principle}</Typography>
                      </Box>
                      <Typography variant="caption">{pc.details}</Typography>
                    </Paper>
                  ))
                }
              </Box>
            )}

            {/* Links to Approval and Non-Conformances */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>References</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
              {selectedResult.approvalId && (
                <Chip
                  icon={<InfoIcon />}
                  label={`Approval: ${selectedResult.approvalId.slice(0, 8)}...`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  clickable
                  onClick={() => {
                    // Navigate to approval workflow detail
                    window.open(`/approvals?id=${selectedResult.approvalId}`, '_blank');
                  }}
                  sx={{ cursor: 'pointer' }}
                />
              )}
              {(selectedResult.nonConformanceIds || []).length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {selectedResult.nonConformanceIds.map((ncId, i) => (
                    <Chip
                      key={i}
                      icon={<WarningIcon />}
                      label={`NC: ${ncId.slice(0, 8)}...`}
                      size="small"
                      variant="outlined"
                      color="error"
                      title={`dVE Non-Conformance ID: ${ncId}`}
                    />
                  ))}
                </Box>
              )}
              {!selectedResult.approvalId && (!selectedResult.nonConformanceIds || selectedResult.nonConformanceIds.length === 0) && !selectedResult.attestation && (
                <Typography variant="body2" color="text.secondary">
                  No approval, attestation, or non-conformance records linked
                </Typography>
              )}
            </Box>

            {/* dVE Attestation Section */}
            {selectedResult.attestation && selectedResult.attestation.attestation_id && (
              <Box sx={{ mb: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FingerprintIcon color="primary" />
                  <Typography variant="subtitle2">dVE Cryptographic Attestation</Typography>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Attestation ID</Typography>
                      <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                        {selectedResult.attestation.attestation_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Attested At</Typography>
                      <Typography variant="body2">
                        {selectedResult.attestation.attested_at ? new Date(selectedResult.attestation.attested_at).toLocaleString() : '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Content Hash (SHA-256)</Typography>
                      <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all', fontSize: '0.7rem' }}>
                        {selectedResult.attestation.content_hash}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Algorithm</Typography>
                      <Typography variant="body2">
                        <Chip label={selectedResult.attestation.algorithm} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Signing Key</Typography>
                      <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                        {selectedResult.attestation.key_id}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            )}

            {/* Phase 4: Verifiable Credential Badge */}
            {selectedResult.credential && selectedResult.credential.credentialId && (
              <Box sx={{ mb: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FingerprintIcon color="secondary" />
                  <Typography variant="subtitle2">Verifiable Credential (W3C)</Typography>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Credential ID</Typography>
                      <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all', fontSize: '0.7rem' }}>
                        {selectedResult.credential.credentialId}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Typography variant="body2">
                        <Chip label={selectedResult.credential.status} color="success" size="small" />
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="caption" color="text.secondary">Issued</Typography>
                      <Typography variant="body2">
                        {selectedResult.credential.issuedAt ? new Date(selectedResult.credential.issuedAt).toLocaleString() : '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            )}

            {/* NC Correction Panel - Only show if entity has NCs */}
            {(selectedResult.nonConformanceIds || []).length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <NCCorrectionPanel
                  entityType={selectedResult.entityType}
                  entityId={selectedResult.entityId}
                  onResubmitSuccess={(result) => {
                    setSnackbar({
                      open: true,
                      message: result.message || 'Entity resubmitted successfully',
                      severity: 'success'
                    });
                    setDrawerOpen(false);
                    loadSessions();
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Drawer>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default VerificationDashboard;
